import { lazy, Suspense, useEffect, useRef, useState, type PropsWithChildren } from "react";
import { Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Link, Stack, Typography } from "@mui/material";
import { useLocation } from "react-router-dom";
import { useAuthContext } from "src/auth/hooks";
import { useTranslate } from "src/locales";
import { communicationCommand, usePendingLegalDocuments } from "src/api/federation-communication";
import uuidv4 from "src/utils/uuidv4";

const AccountDelete = lazy(() => import("src/sections/player/player-profiledata-delete-profile"));
const recoveryPath = /^\/(privacy-policy|terms-and-conditions|auth\/jwt\/(forgot-password|new-password))(\/|$)/;

export default function LegalAcceptanceGate({ children }: PropsWithChildren) {
  const { user, logout } = useAuthContext();
  const { pathname } = useLocation();
  const { t } = useTranslate();
  const pending = usePendingLegalDocuments();
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  const [privacy, setPrivacy] = useState(false);
  const command = useRef(uuidv4());
  const savingLock = useRef(false);
  const document = user?.id ? pending.data?.items[0] : undefined;
  useEffect(() => { setConfirmed(false); setFailed(false); setPrivacy(false); command.current = uuidv4(); }, [user?.id, document?.id, document?.checksum]);
  useEffect(() => {
    const refresh = () => { void pending.mutate(); };
    window.addEventListener("legal-acceptance-required", refresh);
    return () => window.removeEventListener("legal-acceptance-required", refresh);
  }, [pending.mutate]);
  if (!user?.id || recoveryPath.test(pathname)) return <>{children}</>;
  const blocked = Boolean(document || pending.error || pending.isLoading || !pending.data);
  if (!blocked) return <>{children}</>;
  const accept = async () => {
    if (!document || !confirmed || savingLock.current) return;
    savingLock.current = true; setSaving(true); setFailed(false);
    try {
      await communicationCommand("/me/federation/legal/accept", { command_id: command.current, version_id: document.id,
        checksum: document.checksum, capacity: document.guardian_required ? "guardian" : "self", confirmed: true });
      await pending.mutate();
      setConfirmed(false);
    } catch { setFailed(true); await pending.mutate(); }
    finally { savingLock.current = false; setSaving(false); }
  };
  return <Dialog open fullWidth maxWidth="md" disableEscapeKeyDown aria-labelledby="legal-acceptance-title">
    <DialogTitle id="legal-acceptance-title">{t("comm_legal_required")}</DialogTitle>
    <DialogContent dividers>
      <Stack spacing={2}>
        {(pending.error || failed) && <Alert severity="error" action={<Button onClick={() => { void pending.mutate(); }}>{t("retry")}</Button>}>{t("comm_error")}</Alert>}
        {pending.isLoading && <CircularProgress aria-label={t("comm_loading")} />}
        {document && <>
          <Typography variant="subtitle1">{document.provider_name} — {document.title} · {t("comm_version")} {document.version}</Typography>
          <Alert severity="info">{document.notice}</Alert>
          <Box sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", maxHeight: { xs: "35vh", md: "45vh" }, overflow: "auto", p: 2, bgcolor: "background.neutral" }} tabIndex={0} role="region" aria-label={document.title}>{document.body}</Box>
          <Typography variant="body2">{t("comm_remaining", { count: pending.data?.items.length ?? 0 })}</Typography>
          <FormControlLabel control={<Checkbox checked={confirmed} onChange={(_, value) => setConfirmed(value)} disabled={saving} />}
            label={t(document.guardian_required ? "comm_confirm_guardian" : "comm_confirm_self")} />
        </>}
        <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
          <Link href="/privacy-policy">{t("privacy_policy")}</Link>
          <Button size="small" onClick={() => setPrivacy(!privacy)}>{t("comm_account_options")}</Button>
        </Stack>
        {privacy && <Suspense fallback={<CircularProgress />}><AccountDelete /></Suspense>}
      </Stack>
    </DialogContent>
    <DialogActions sx={{ flexWrap: "wrap", gap: 1 }}>
      <Button onClick={() => { void logout(); }} disabled={saving}>{t("logout")}</Button>
      <Button variant="contained" onClick={() => { void accept(); }} disabled={!confirmed || saving || !document || Boolean(pending.error)}>{t("comm_accept")}</Button>
    </DialogActions>
  </Dialog>;
}
